import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { MGR_ROLES, PAYROLL_ROLES } from "../api";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import AIAssistant from "../components/AIAssistant";
import {
  LayoutDashboard, Users, Umbrella, Clock, Wallet, ReceiptText, CheckSquare,
  Settings, ShieldHalf, Moon, Sun, Menu, LogOut, UserRound, Eye, XCircle,
} from "lucide-react";

export default function DashboardLayout() {
  const { user, tenant, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const viewTenantName = sessionStorage.getItem("view_tenant_name");

  const exitViewAs = () => {
    sessionStorage.removeItem("view_tenant");
    sessionStorage.removeItem("view_tenant_name");
    window.location.href = "/admin/tenants";
  };

  const toggleDark = () => {
    setDark(!dark);
    document.documentElement.classList.toggle("dark");
  };

  const brandColor = tenant?.branding?.primary_color || "#0B0F17";
  const company = tenant?.company_name || "WORKAHOLIC HR Solution";

  const nav = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), testid: "nav-item-dashboard", show: true },
    { to: "/my-portal", icon: UserRound, label: t("nav.myportal"), testid: "nav-item-myportal", show: true },
    { to: "/employees", icon: Users, label: t("nav.employees"), testid: "nav-item-employees", show: MGR_ROLES.includes(user?.role) },
    { to: "/leave", icon: Umbrella, label: t("nav.leave"), testid: "nav-item-leave", show: true },
    { to: "/attendance", icon: Clock, label: t("nav.attendance"), testid: "nav-item-attendance", show: true },
    { to: "/claims", icon: ReceiptText, label: t("nav.claims"), testid: "nav-item-claims", show: true },
    { to: "/payroll", icon: Wallet, label: t("nav.payroll"), testid: "nav-item-payroll", show: PAYROLL_ROLES.includes(user?.role) },
    { to: "/approvals", icon: CheckSquare, label: t("nav.approvals"), testid: "nav-item-approvals", show: MGR_ROLES.includes(user?.role) },
    { to: "/settings", icon: Settings, label: t("nav.settings"), testid: "nav-item-whitelabel", show: ["TENANT_ADMIN", "HR_ADMIN", "SUPER_ADMIN"].includes(user?.role) },
    { to: "/admin/tenants", icon: ShieldHalf, label: t("nav.admin"), testid: "nav-item-admin-tenants", show: user?.role === "SUPER_ADMIN" },
  ];

  const NavItems = () => (
    <>
      {nav.filter((n) => n.show).map((n) => (
        <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={() => setOpen(false)}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
          <n.icon className="w-4.5 h-4.5 w-[18px] h-[18px]" /> {n.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col bg-slate-900 text-white p-4 z-40">
        <div className="flex items-center gap-2 px-2 py-3">
          {tenant?.branding?.logo_url ? (
            <img src={tenant.branding.logo_url} alt="logo" className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold" style={{ backgroundColor: brandColor }}>
              {company[0]}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-display font-bold text-sm truncate" data-testid="sidebar-company-name">{company}</p>
            <p className="text-[11px] text-slate-400">{tenant?.plan || "PLATFORM"}</p>
          </div>
        </div>
        <nav className="mt-5 space-y-1 flex-1"><NavItems /></nav>
        <p className="text-[10px] text-slate-500 px-2">WORKAHOLIC HR Solution · v1.0</p>
      </aside>

      <div className="lg:pl-60 flex flex-col min-h-screen">
        {viewTenantName && (
          <div data-testid="view-as-tenant-banner" className="bg-amber-400 text-amber-950 px-4 py-2 text-xs sm:text-sm font-semibold flex items-center justify-center gap-3">
            <Eye className="w-4 h-4 shrink-0" />
            <span>{t("admin.viewas")}: <b>{viewTenantName}</b></span>
            <button data-testid="exit-view-as-button" onClick={exitViewAs}
              className="flex items-center gap-1 underline underline-offset-2 hover:text-amber-700">
              <XCircle className="w-4 h-4" /> {t("admin.exitview")}
            </button>
          </div>
        )}
        <header className="glass-header flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-slate-900 text-white p-4">
                <p className="font-display font-bold px-2 py-3">{company}</p>
                <nav className="space-y-1"><NavItems /></nav>
              </SheetContent>
            </Sheet>
            <span className="font-display font-semibold text-sm hidden sm:block">{company}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div data-testid="lang-toggle-bm-en" className="flex items-center border rounded-full p-0.5 bg-slate-50 dark:bg-slate-800">
              {["bm", "en"].map((l) => (
                <button key={l} data-testid={`lang-option-${l}`} onClick={() => setLang(l)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-blue-800 text-white" : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
                  {l}
                </button>
              ))}
            </div>
            <Button data-testid="theme-toggle" variant="ghost" size="icon" onClick={toggleDark}>
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-blue-800 text-white text-xs">{(user?.name || "U")[0]}</AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-semibold leading-tight">{user?.name}</p>
                    <p className="text-[10px] text-slate-500 leading-tight">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/my-portal")}><UserRound className="w-4 h-4 mr-2" />{t("nav.myportal")}</DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-button" onClick={logout}><LogOut className="w-4 h-4 mr-2" />{t("common.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <AIAssistant />
    </div>
  );
}
